/**
 * Shared topic definitions for the AI assistant.
 *
 * Used by:
 * - AssistantPopover (home page entry point)
 * - AI Assistant V2 landing page (suggestion cards)
 *
 * Adding a new topic here automatically surfaces it in both entry points.
 * The `headline` field drives the V2 landing card header text when the
 * user selects a topic — sourced from Figma nodes 364:5917, 380:5962,
 * 380:5986, 380:6010.
 */

import type { LucideIcon } from 'lucide-react';
import { BookOpen, UserPlus, HelpCircle, Compass } from 'lucide-react';

export interface AssistantTopic {
  /** Short label shown in popover and suggestion cards */
  label: string;
  /** Lucide icon component — rendered at whatever size the consumer needs */
  icon: LucideIcon;
  /** Prompt text sent to the assistant when this topic is selected */
  prompt: string;
  /** Full headline displayed in the V2 landing card (from Figma) */
  headline: string;
}

/**
 * The canonical list of assistant entry-point topics.
 *
 * Order matters — determines display order in both the popover and
 * suggestion cards. Keep synced with Figma landing page variants.
 */
export const ASSISTANT_TOPICS: readonly AssistantTopic[] = [
  {
    // "Learn to Meditate" over "Learn Sudarshan Kriya" (owner, 2026-07-16):
    // newcomers think "meditation", not insider vocabulary — Sara introduces
    // SKY herself from the broader ask.
    label: 'Learn to Meditate',
    icon: BookOpen,
    prompt: 'I want to learn to meditate',
    headline:
      "You're Ready to Experience the Practice Millions Trust for Calm & Clarity",
  },
  {
    // "Sign up" over "Register" (owner, 2026-07-16): more natural US phrasing,
    // and it matches the headline below that already said "Signed Up".
    label: 'Sign up for a Course',
    icon: UserPlus,
    prompt: 'Help me sign up for a course',
    headline: "Let's Get You Signed Up — Just a Few Simple Steps Left",
  },
  {
    label: 'I have Questions',
    icon: HelpCircle,
    prompt: 'I have questions about Art of Living',
    headline:
      "Curious? You're in Good Company — Let's Explore Your Questions Together",
  },
  {
    // "Explore Programs" over "Explore AOL": to a US audience "AOL" reads as
    // America Online before Art of Living.
    label: 'Explore Programs',
    icon: Compass,
    prompt: 'Tell me about Art of Living programs',
    headline:
      'Explore a Global Movement — From World Culture Festivals to Peace & Service Initiatives',
  },
] as const;

/** Default headline when no topic is selected */
export const DEFAULT_HEADLINE = 'Hi! How can I help you today?';
